/**
 * The layout spec for each ISA template type, from UtlCfg_DSTemplate: one row per template +
 * sheet + zone, carrying the form's printed line range.
 *
 * Sheet_No 0 is a zone shared by every sheet (center, header, rev). Application Specific is its
 * own template and is always the last sheet of any multi-sheet datasheet, so a template's own
 * rows describe only its content sheets.
 *
 * A template type with no spec rows gets a null layout, and the canvases fall back to inferring
 * rows from the boxes present.
 *
 * Pure functions only: the PDF worker imports this, so it must not pull in React or the API
 * client. Loading the spec lives in useDsTemplates.js.
 */

const APP_SPEC = 'Application Specific';

/**
 * The zones that apply to one sheet of one template, as { [dsZone]: { lineFrom, lineTo, rowCount } }.
 * Returns null when the template type has no spec, so the caller can fall back.
 *
 * formName is the template's own form number (DS_Name, e.g. 20C2111). Spec rows under that name
 * are a per-form override: for any sheet the form defines, its rows replace the type's rows for
 * that sheet outright. Many instrument forms move the body split and the cal/comp blocks, so the
 * type's ranges are only the default.
 */
export function sheetLayout(specRows, tempType, sheet, sheetCount, formName = null) {
  if (!Array.isArray(specRows) || !tempType) return null;
  const own = specRows.filter(r => r.templateName === tempType);
  if (own.length === 0) return null;
  const form = formName ? specRows.filter(r => r.templateName === formName) : [];
  const rowsFor = sheetNo => {
    const override = form.filter(r => r.sheetNo === sheetNo);
    return override.length ? override : own.filter(r => r.sheetNo === sheetNo);
  };

  const isAppSpec = sheetCount > 1 && sheet === sheetCount;
  const sheetRows = isAppSpec
    ? specRows.filter(r => r.templateName === APP_SPEC && r.sheetNo === 1)
    : rowsFor(sheet);
  const shared = rowsFor(0);

  const zones = {};
  for (const r of [...shared, ...sheetRows]) {
    zones[r.dsZone] = { lineFrom: r.lineFrom, lineTo: r.lineTo, rowCount: r.rowCount };
  }
  return zones;
}

// The revision block is unnumbered, so the spec sizes it by Row_Count. Five is the ISA form's
// count, and what every sheet printed before the spec existed.
const DEFAULT_REV_ROWS = 5;

/** Indexes of the revision block's data rows for this sheet: [0 .. Row_Count-1]. */
export function revRows(layout) {
  const n = layout?.rev?.rowCount ?? DEFAULT_REV_ROWS;
  return Array.from({ length: Math.max(0, n) }, (_, i) => i);
}

/**
 * The Calibrations and Component Identifications rows for this sheet, in the shape the canvases
 * draw: each block is a heading line, a column-heading line, then data lines to Line_To. The
 * column shell is the same on every ISA form; only where the block sits and how many data rows
 * it has vary (instrument cal 110-117, valve 310-331). A sheet without the zones gets [].
 * Returns null with no layout, so the caller keeps its own fallback.
 */
export function sectionRows(layout) {
  if (!layout) return null;
  const rows = [];
  const add = (spec, head, sub, data) => {
    if (spec?.lineFrom == null) return;
    rows.push({ type: head, lineNo: spec.lineFrom });
    if (spec.lineTo > spec.lineFrom) rows.push({ type: sub, lineNo: spec.lineFrom + 1 });
    for (let n = spec.lineFrom + 2; n <= spec.lineTo; n += 1) rows.push({ type: data, lineNo: n });
  };
  add(layout.cal,  'cal-group-header', 'cal-subheader',  'cal-data');
  add(layout.comp, 'comp-header',      'comp-subheader', 'comp-data');
  return rows;
}

/** Every sheet's layout at once, keyed by sheet number - the serialisable form the PDF worker takes. */
export function sheetLayouts(specRows, tempType, sheetCount, formName = null) {
  const out = {};
  for (let s = 1; s <= sheetCount; s += 1) out[s] = sheetLayout(specRows, tempType, s, sheetCount, formName);
  return out;
}
